import { failureNotification } from "../store/notification.duck";
import { NotificationMessages } from "@shared/models/notification.models";

export function safePrintError(err: any, dispatch: any, fallBackMessage?: string) {
  try {
    if (err.message) {
      const parsed = JSON.parse(err.message);
      if (parsed.message) {
        dispatch(failureNotification(parsed.message));
        return;
      }
    }
  } catch (e: any) {

  }
  dispatch(failureNotification(fallBackMessage || 'Action failed'));
}

export function parseErrorFromException(err: any): string {
  try {
    return JSON.parse(err.message).message
  } catch (e) {
    TrackJS?.track(`Unknown error: ${JSON.stringify(err)}`);
    return `Unknown error: ${JSON.stringify(err)}`;
  }
}

export enum ErrorCode {
  VerificationRequired,
  Unknown,
  TeamNameAlreadyExists,
  EmailAlreadyExists,
  FailedLogin,
  OfficeContainsArea,
  LoginRequired,
  UserDeleted,
  UserAlreadyInAccount,
}

export interface Error {
  title: string;
  description: string;
  id: ErrorCode;
}

const messages: any = {
  'Email verification required': {
    title: 'Email verification required',
    description: 'Check your email for a verification email.',
    id: ErrorCode.VerificationRequired,
  },
  'default': {
    title: 'Unknown Error',
    description: '',
    id: ErrorCode.Unknown,
  },
  'unk': {
    title: 'Unknown Error',
    description: '',
    id: ErrorCode.Unknown,
  },
  'Team already exists': {
    title: 'Team already exists',
    description: 'You already have a team with that name.',
    id: ErrorCode.TeamNameAlreadyExists,
  },
  'Email already exists': {
    title: 'Email already exists',
    description: 'That email address is already taken.',
    id: ErrorCode.EmailAlreadyExists,
  },
  // Must match EmailAlreadyExistsException.USER_IN_SAME_COMPANY_MESSAGE on the backend.
  'This account already exists. Go to Settings > Manage Users to assign them to the correct team.': {
    title: 'This account already exists',
    description: 'Go to Settings > Manage Users to assign them to the correct team.',
    id: ErrorCode.UserAlreadyInAccount,
  },
  // Must match EmailAlreadyExistsException.USER_DELETED_MESSAGE on the backend.
  'Account already exists. This user was previously deleted. You can restore their access under Settings > Manage Users > Deleted Users.': {
    title: 'Account already exists',
    description: 'This user was previously deleted. You can restore their access under Settings > Manage Users > Deleted Users.',
    id: ErrorCode.UserDeleted,
  },
  'Failed login': {
    title: 'Invalid email or password',
    description: "Try clicking 'Forgot Password' if you're having trouble logging in.",
    id: ErrorCode.FailedLogin,
  },
  'Login failed - account users Google': {
    title: 'Wrong type of log in used.',
    description: "This account uses Google to log in. Select 'Log in with Google' and follow the steps to log into this account.",
    id: ErrorCode.FailedLogin,
  },
  'Login failed - account users Microsoft 365': {
    title: 'Wrong type of log in used.',
    description: "This account uses Microsoft 365 to log in. Select 'Log in with Microsoft 365' and follow the steps to log into this account.",
    id: ErrorCode.FailedLogin,
  },
  'This office contains an area': {
    title: 'This office contains an area.',
    description: "You cannot delete an office with an associated area. Please contact hello@team-today.com to make amends to this office.",
    id: ErrorCode.OfficeContainsArea,
  },
  'reg': {
    title: 'No account registered.',
    description: "You can register for an account or ask a colleague for an invite. If you think you already have an account please contact hello@team-today.com.",
    id: ErrorCode.OfficeContainsArea,
  },
  'azu': {
    title: 'Failed to log in with Microsoft.',
    description: "Please try again. If the problem persists, contact hello@team-today.com.",
    id: ErrorCode.FailedLogin,
  },
  'goog': {
    title: 'Failed to log in with Google.',
    description: "Please try again. If the problem persists, contact hello@team-today.com.",
    id: ErrorCode.FailedLogin,
  },
  'declined': {
    title: "Microsoft sign-in wasn't approved.",
    description: "Team Today needs your permission to sign you in. Try again and select Accept on the Microsoft screen. If Microsoft says admin approval is needed, ask your IT admin to approve Team Today.",
    id: ErrorCode.FailedLogin,
  },
  'notAssigned': {
    title: "Your organisation hasn't given you access to Team Today.",
    description: "Ask your IT admin to assign you to the Team Today app in Microsoft Entra ID (Enterprise applications > Team Today > Users and groups).",
    id: ErrorCode.FailedLogin,
  },
  'caBlock': {
    title: "Your organisation's sign-in policy blocked this sign-in.",
    description: "A Microsoft Entra Conditional Access policy (for example a device or location rule) stopped the sign-in. Contact your IT admin.",
    id: ErrorCode.FailedLogin,
  },
  'appBlock': {
    title: "Team Today is blocked in your organisation's Microsoft tenant.",
    description: "Ask your IT admin to enable the Team Today app in Microsoft Entra ID, or contact hello@team-today.com.",
    id: ErrorCode.FailedLogin,
  },
  'network': {
    title: "Couldn't reach the sign-in service.",
    description: "Check your internet connection and try again. A VPN or corporate firewall may be blocking Microsoft or Team Today.",
    id: ErrorCode.FailedLogin,
  },
  '401': {
    title: '',
    description: "",
    id: ErrorCode.LoginRequired,
  },
  'canByUser': {
    title: 'Failed to log in with Microsoft.',
    description: "This step is required to advance, a popup will appear and you can use your Microsoft account to log in. If you have any questions contact us at hello@team-today.com",
    id: ErrorCode.FailedLogin,
  },
  'consent': {
    title: 'Admin approval needed.',
    description: 'Your organisation has not yet approved Team Today. Please open Team Today at app.team-today.com to complete sign-in, or ask your IT admin to approve the app.',
    id: ErrorCode.FailedLogin,
  },
  'interact': {
    title: "Sign-in can't complete inside Outlook.",
    description: 'Please open Team Today at app.team-today.com to finish signing in. Once signed in there, returning to Outlook will work.',
    id: ErrorCode.FailedLogin,
  },
  'embBlock': {
    title: "Sign-in popup couldn't open.",
    description: 'Outlook prevented the sign-in window from opening. Please use the web version at app.team-today.com to sign in.',
    id: ErrorCode.FailedLogin,
  },
}

// Message plus HTTP response body: rawUserRequest skips agent error processing,
// so a backend rejection arrives as a superagent error whose message may only be
// the status text ("Unauthorized") with the real reason in response.text.
function authErrorText(err: any): string {
  const text = [err?.message, err?.response?.text].filter(Boolean).join(' ');
  return text || (err?.toString?.() ?? '').toString();
}

export function classifyAuthError(err: any): NotificationMessages {
  const msg = authErrorText(err);
  // Thunk rejections come through unwrap() serialized, with MSAL's errorCode moved
  // to `code` (see serializeAuthError in authMachine.duck).
  const code = (err?.errorCode ?? err?.code) as string | undefined;

  if (msg.includes('CancelledByUser')) return NotificationMessages.cancelledByUser;
  if (msg.includes('User not registered')) return NotificationMessages.noAccountFound;

  if (msg.includes('AADSTS50105')) return NotificationMessages.notAssignedToApp;
  if (/AADSTS530\d\d/.test(msg)) return NotificationMessages.blockedByPolicy;
  if (msg.includes('AADSTS700016')
    || msg.includes('AADSTS7000112')
    || msg.includes('unauthorized_client')) {
    return NotificationMessages.appBlockedInTenant;
  }

  if (code === 'consent_required'
    || msg.includes('AADSTS65001')
    || msg.includes('AADSTS90094')
    || msg.includes('consent_required')
    || msg.includes('Consent required')
    || msg.includes('resourceRequiresConsent')) {
    return NotificationMessages.consentRequired;
  }

  // AADSTS65004: the user cancelled the consent screen (or the "Need admin
  // approval" page) and Entra redirected back with error=access_denied.
  if (msg.includes('AADSTS65004') || msg.includes('access_denied')) {
    return NotificationMessages.consentDeclined;
  }

  if (msg.includes('URL was unable to be opened in embedded browser')
    || msg.includes('FailedToOpenWindow')) {
    return NotificationMessages.embeddedBrowserBlocked;
  }

  if (code === 'interaction_required'
    || msg.includes('interaction_required')
    || msg.includes('login_required')) {
    return NotificationMessages.interactionRequired;
  }

  if (code === 'network_error'
    || msg.includes('Failed to fetch')
    || msg.includes('NetworkError')
    || msg.includes('Request has been terminated')) {
    return NotificationMessages.networkError;
  }

  return NotificationMessages.unknownError;
}

// Short reason a user can read out to support or their IT admin: the Entra
// AADSTS code when there is one, else the error code / HTTP status, else the
// start of the message.
export function describeAuthError(err: any): string | undefined {
  const msg = authErrorText(err);
  const aadsts = msg.match(/AADSTS\d+/)?.[0];
  if (aadsts) return aadsts;

  const code = err?.errorCode ?? err?.code;
  const status = err?.status ?? err?.response?.status;
  const parts = [code, status && `HTTP ${status}`].filter(Boolean);
  if (parts.length) return parts.join(', ');

  return msg.trim().slice(0, 120) || undefined;
}

function defaultError(message: string) {
  return {
    title: message,
    description: message,
    id: ErrorCode.Unknown,
  }
}

export function enrichError(message: string | undefined): Error {
  if (!message) {
    return messages['default'];
  }
  if (messages[message]) {
    return messages[message]
  } else {
    return defaultError(message);
  }
}
